import { useCallback } from "react";

import { useTourTargetContext } from "./OrientationTour";
import type { TourStepId } from "./tourSteps";

/**
 * Returns a callback ref that registers the host element as the anchor for
 * the given orientation-tour step. Attach it to any DOM-bearing component
 * to make that element the tour anchor for the step.
 */
export function useTourStep(id: TourStepId) {
  const { registerTarget } = useTourTargetContext();
  return useCallback(
    (el: HTMLElement | null) => {
      registerTarget(id, el);
    },
    [id, registerTarget],
  );
}

/**
 * Returns a callback that dismisses `id` if it's the step currently on screen.
 *
 * For gesture-teaching steps: call it when the user performs the gesture, so
 * the tooltip retires itself instead of lingering over an instruction the user
 * has already followed. Safe to call unconditionally — it no-ops when the tour
 * isn't running or a different step is active.
 */
export function useCompleteTourStep(id: TourStepId) {
  const { completeStep } = useTourTargetContext();
  return useCallback(() => {
    completeStep(id);
  }, [id, completeStep]);
}
